import React from "react";
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0"); // 월이 한 자리면 앞에 0 추가
  const day = String(date.getDate()).padStart(2, "0"); // 일이 한 자리면 앞에 0 추가
  return `${month}/${day}`; // 원하는 형식으로 반환
};

export const showType = (assignmentType, sectionType) => {
  if (assignmentType === "vocabulary") {
    return "";
  }
  if (assignmentType === "paragraph") {
    return sectionType;
  }
  const typeMap = {
    analysis: {
      ebook: "책 보기",
      keypoints: "핵심정리",
      reading: "지문읽기",
      direct_translation: "직독직해",
      words: "단어학습",
      syntax_analysis: "구문분석",
      flow: "흐름정리",
      grammar: "문법",
      sentence_test: "문장테스트",
    },
    comprehensive: {
      comprehensive: "내용이해 워크북",
      keyword: "단어 테스트",
      understanding: "서술형 연습",
      ox: "OX 퀴즈",
      translation: "해석 연습",
      summary: "요약 연습",
      scramble: "페러프레이징",
      book: "원서 워크북",
      chapter_book: "챕터북",
    },
    readers_book: {
      ebook: "전자책",
    },
    grammar_bank: {
      ebook: "책 보기",
      grammar: "문법",
    },
    sentence: {
      blank: "빈칸 채우기",
      scramble: "스크램블",
      composition: "영작",
      translation: "해석",
      dictation: "받아쓰기",
      comprehensive: "종합",
    },
  };
  return typeMap[assignmentType]?.[sectionType] || sectionType;
};

// 개발 서버 표시 띠가 body 를 아래로 밀어낸 만큼(--dev-bar-height, 운영은 0) 빼 준다.
// 빼지 않으면 개발에서만 페이지가 그 높이만큼 넘쳐 스크롤바가 생긴다.
//
// --study-extra-offset 은 위에 무언가를 더 얹은 화면이 그 높이만큼 줄이게 하는 자리다.
// 기본 0 이고, 지문분석의 '문법' 단계만 단계 바를 어법 화면 위에 얹으면서 쓴다
// (AnalysisPage.jsx). CSS 변수라 그 화면 아래에만 걸린다.
export const getContentHeight = (isMobile = false) => {
  if (isMobile)
    return "calc(100vh - 80px - var(--dev-bar-height) - var(--study-extra-offset, 0px))";
  return "calc(100vh - 140px - var(--dev-bar-height) - var(--study-extra-offset, 0px))";
};

export const splitIntoChunks = (text, maxWords = 30) => {
  const chunks = [];

  const words = text.split(" ");
  for (let j = 0; j < words.length; j += maxWords) {
    chunks.push(words.slice(j, j + maxWords).join(" "));
  }

  return chunks;
};

export const boldText = ({ text }) => {
  if (!text) {
    return null;
  }

  const pattern = /--\*\*(.*?)\*\*--/g;
  const parsed = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    parsed.push(text.substring(lastIndex, match.index));
    parsed.push(<strong key={parsed.length}>{match[1]}</strong>);
    lastIndex = pattern.lastIndex;
  }

  parsed.push(text.substring(lastIndex));

  return <>{parsed}</>;
};

export const convertCircledNumberToDigit = (str) => {
  const unicodeOffset = "①".charCodeAt(0) - 1; // ①의 코드 포인트(9312)에서 1을 뺀 값
  return str.charCodeAt(0) - unicodeOffset;
};

// 5지선다 정답 앞머리의 번호들. "③" → [3], "③ Destroying → Designing" → [3], "③, ④" → [3, 4], "ⓔ wise" → [5].
// 학원이 만든 모의고사 유형(custom_*, 2026-10)은 정답 뒤에 고친 말 · 개수를 덧붙이거나 두 개를 고르게 한다.
// 앞머리에 번호가 없으면 빈 배열.
export const parseChoiceAnswerList = (answer) => {
  const head = String(answer ?? "").trim().match(/^((?:[\u2460-\u2469\u24D0-\u24D9]|[1-9])(?:\s*,\s*(?:[\u2460-\u2469\u24D0-\u24D9]|[1-9]))*)/);
  if (!head) return [];
  return head[1].split(",").map((one) => {
    const mark = one.trim();
    const code = mark.charCodeAt(0);
    if (code >= 0x2460 && code <= 0x2469) return code - 0x2460 + 1;
    if (code >= 0x24d0 && code <= 0x24d9) return code - 0x24d0 + 1;
    return Number(mark);
  });
};

export const convertDigitToCircledNumber = (num) => {
  if (num < 1 || num > 10) {
    throw new Error("Input must be between 1 and 10");
  }
  const unicodeOffset = "①".charCodeAt(0) - 1; // ①의 코드 포인트(9312)에서 1을 뺀 값
  return String.fromCharCode(unicodeOffset + num);
};

// 지인에듀 본문 표기를 우리 태그로 바꾼다.
//   `--낱말--`   밑줄  → <u>
//   `**낱말**`   굵게  → <b>
// 둘이 겹친 `--**낱말**--` 이 있어서 **겹친 것을 먼저** 처리한다. 나중에 하면
// <u> 안에 `**` 가 남아 applyUnderline 이 통째로 밑줄로 집어 먹고 별표가 글자로
// 보인다 (2026-09-13 모의고사 신유형에서 별표가 그대로 노출됐다).
export const ginEduMarkupToTag = (text) => {
  return String(text ?? "")
    .replace(/--\*\*(.*?)\*\*--/g, "<u>$1</u>")
    .replace(/--(.*?)--/g, "<u>$1</u>")
    .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
};

export const applyUnderline = (text) => {
  // '<u>텍스트</u>'(밑줄+굵게)와 '<b>텍스트</b>'(굵게만) 부분을 분리.
  // <b> 는 문제뱅크 문법의 고르는 자리 `[are/is]` 가 쓴다 (bank_to_assignment.py).
  const parts = text.split(/(<u>.*?<\/u>|<b>.*?<\/b>)/g);

  return parts.map((part, index) => {
    if (part.startsWith("<u>") && part.endsWith("</u>")) {
      return (
        <u key={index}>
          <strong>{part.replace(/<\/?u>/g, "")}</strong>
        </u>
      ); // '<u>'와 '</u>' 제거 후 JSX 변환
    }
    if (part.startsWith("<b>") && part.endsWith("</b>")) {
      return <strong key={index}>{part.replace(/<\/?b>/g, "")}</strong>;
    }
    return part; // 일반 텍스트는 그대로 반환
  });
};

// [모의고사 밤티 수술] --**(A)**-- 마커를 밑줄 빈칸 칩으로 렌더.
// boldText와 동일 패턴(/--\*\*(.*?)\*\*--/g). 마커 밖 텍스트는 applyUnderline을
// 그대로 태워 기존 밑줄 표시를 보존한다. 데이터·저작 도구 무변경.
export const renderWithBlanks = (text) => {
  // [표시 보정] 데이터에 이스케이프(\" \')가 섞여 오면 백슬래시가 화면에
  // 노출됨(서술형 본문 재현) → 표시 직전에만 복원. 데이터 무변경.
  const cleaned = String(text ?? "").replace(/\\"/g, '"').replace(/\\'/g, "'");
  const parts = cleaned.split(/--\*\*(.*?)\*\*--/g);
  if (parts.length === 1) return applyUnderline(cleaned);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span
        key={"bk" + i}
        style={{
          display: "inline-block",
          minWidth: "64px",
          borderBottom: "2px solid #B9C1C9",
          borderRadius: "6px 6px 0 0",
          backgroundColor: "#F7FAF9",
          padding: "0 10px",
          textAlign: "center",
          fontWeight: 800,
          color: "#7A8490",
        }}
      >
        {part}
      </span>
    ) : (
      <React.Fragment key={"tx" + i}>{applyUnderline(part)}</React.Fragment>
    )
  );
};

export const getTextByLabel = (text, label) => {
  const regex = new RegExp(`--${label} (.*?)--`);
  const match = text.match(regex);
  return match ? match[1] : null; // 매칭된 그룹이 있으면 반환, 없으면 null 반환
};

export const fillColorAtSubstance = (text, color) => {
  const match = text.match(/\[(.*?)\]/); // [] 안의 텍스트 찾기
  if (match) {
    const beforeBracket = text.slice(0, match.index);
    const bracketedText = match[0]; // 대괄호 포함된 전체 문자열
    const afterBracket = text.slice(match.index + bracketedText.length);
    return (
      <>
        <span
          style={{
            color: color,
            fontWeight: "bold",
          }}
        >
          {beforeBracket}
          {bracketedText}
        </span>
        {afterBracket}
      </>
    );
  }
  return text;
};

export const formatFullDateString = ({ dateString, defaultString }) => {
  if (!dateString) return defaultString;
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // 월이 한 자리면 앞에 0 추가
  const day = String(date.getDate()).padStart(2, "0"); // 일이 한 자리면 앞에 0 추가
  const hours = String(date.getHours()).padStart(2, "0"); // 시간이 한 자리면 앞에 0 추가
  const minutes = String(date.getMinutes()).padStart(2, "0"); // 분이 한 자리면 앞에 0 추가
  const seconds = String(date.getSeconds()).padStart(2, "0"); // 초가 한 자리면 앞에 0 추가
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`; // 원하는 형식으로 반환
};

export const showGrade = (grade) => {
  const gradeMap = {
    1: "초1",
    2: "초2",
    3: "초3",
    4: "초4",
    5: "초5",
    6: "초6",
    7: "중1",
    8: "중2",
    9: "중3",
    10: "고1",
    11: "고2",
    12: "고3",
  };

  return gradeMap[grade] || grade;
};

export const roundScoreValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return Math.round(parsedValue * 100) / 100;
};

export const formatScore = (value) => {
  const roundedScore = roundScoreValue(value);
  if (roundedScore === null) {
    return "";
  }

  return roundedScore.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};

/**
 * Normalizes English text for answer comparison by:
 * - Converting to lowercase
 * - Removing all spaces
 * - Removing all punctuation marks
 */
export const normalizeEnglishAnswer = (text) => {
  if (!text) return "";
  return (
    text
      // 모바일 키보드의 둥근 따옴표/아포스트로피(' ' " ")를 일반형으로 통일
      .normalize("NFKC")
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u0060\u00B4]/g, "'")
      .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
      .toLowerCase()
      .replace(/[\s]/g, "") // Remove all whitespace
      .replace(/[.,!?;:'"()\-–—…]/g, "") // Remove common punctuation
  );
};

/**
 * Normalizes sentence-style answers for robust exact-match checks.
 * - Applies Unicode normalization to reduce visually identical character mismatches
 * - Collapses repeated whitespace
 * - Removes spaces right before punctuation
 * - Optionally lowercases text for case-insensitive comparison
 */
export const normalizeSentenceAnswer = (text, { ignoreCase = true } = {}) => {
  if (!text) return "";

  const normalized = text
    .normalize("NFKC")
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .replace(/\s+([,.!?;:])/g, "$1")
    .trim();

  return ignoreCase ? normalized.toLowerCase() : normalized;
};

/**
 * Parses paragraph:summary_blank markup such as
 * "prefix --**a**　　　-- suffix" into displayable sentence parts.
 */
export const parseParagraphSummaryBlank = (text) => {
  const source = String(text || "");
  const blankMatch = source.match(/--([\s\S]*?)--/);

  if (!blankMatch) {
    return { prefix: source, hint: "", suffix: "" };
  }

  const hintMatch = blankMatch[1].match(/\*\*(.*?)\*\*/);

  return {
    prefix: source.slice(0, blankMatch.index),
    hint: hintMatch ? hintMatch[1] : "",
    suffix: source.slice(blankMatch.index + blankMatch[0].length),
  };
};

// "(A) reveal / (B) instantaneously / (C) demand" 처럼 라벨이 붙은 여러 칸 정답을 나눈다.
// 지인에듀 모의고사의 표 완성·지칭 서술 유형이 이 모양이다(2026-10-12). 라벨이 없으면 빈 목록.
export const parseLabeledAnswer = (text) =>
  [...String(text ?? "").matchAll(/\(([A-Z])\)\s*([^/]+?)\s*(?=\/|$)/g)].map((matched) => ({
    label: `(${matched[1]})`,
    value: matched[2].trim(),
  }));

// 지인에듀가 표를 파이썬 리스트 글자("[['Aspect', 'Market'], ...]")로 보낸다. 이미 배열이면 그대로 쓴다.
// 작은따옴표 안의 아포스트로피(\')와 \xa0 같은 이스케이프를 풀어야 해서 JSON.parse 로는 안 된다.
export const parseTableText = (value) => {
  if (Array.isArray(value)) return value;
  const text = String(value ?? "");
  const stack = [];
  let root = null;
  let index = 0;
  while (index < text.length) {
    const character = text[index];
    if (character === "[") {
      const row = [];
      if (stack.length) stack[stack.length - 1].push(row);
      else root = row;
      stack.push(row);
      index += 1;
    } else if (character === "]") {
      stack.pop();
      index += 1;
    } else if (character === "'" || character === '"') {
      let cell = "";
      index += 1;
      while (index < text.length && text[index] !== character) {
        if (text[index] === "\\" && index + 1 < text.length) {
          const escaped = text[index + 1];
          if (escaped === "x" && /^[0-9a-fA-F]{2}$/.test(text.slice(index + 2, index + 4))) {
            cell += String.fromCharCode(parseInt(text.slice(index + 2, index + 4), 16));
            index += 4;
            continue;
          }
          cell += escaped === "n" ? "\n" : escaped;
          index += 2;
          continue;
        }
        cell += text[index];
        index += 1;
      }
      if (stack.length) stack[stack.length - 1].push(cell);
      index += 1;
    } else {
      index += 1;
    }
  }
  return Array.isArray(root) ? root.filter(Array.isArray) : [];
};
